import type { Product } from '~/store/newproducts';

/**
 * Fonction pour comparer deux valeurs de "Première collection"
 * Format: 2025H, 2025E, 2025S1, 2025S2, etc.
 * S1 est assimilé à E, S2 à H.
 * Ordre décroissant (podium): 2027E > 2026H > 2026E > 2025H > 2025E > ...
 * Les produits les plus récents apparaissent en premier
 *
 * @param a - Première valeur à comparer
 * @param b - Deuxième valeur à comparer
 * @returns - Nombre négatif si a > b, positif si a < b, 0 si égal
 */
export function compareCollectionTri(a: string | undefined, b: string | undefined): number {
  // Si les deux valeurs sont vides, elles sont égales
  if (!a && !b) return 0;

  // Les valeurs vides vont à la fin (ordre décroissant)
  if (!a) return 1;
  if (!b) return -1;

  // Extraire l'année et la saison (S1 → E, S2 → H)
  const parseCollection = (value: string) => {
    const match = value.match(/^(\d{4})(H|E|S1|S2)$/);
    if (!match) return { year: 0, season: 'Z' as const }; // Valeurs invalides à la fin

    const seasonMap = { H: 'H', E: 'E', S1: 'E', S2: 'H' } as const;
    return {
      year: parseInt(match[1], 10),
      season: seasonMap[match[2] as keyof typeof seasonMap],
    };
  };

  const collectionA = parseCollection(a);
  const collectionB = parseCollection(b);

  // Comparer par année d'abord (ordre DÉCROISSANT - du plus récent au moins récent)
  if (collectionA.year !== collectionB.year) {
    return collectionB.year - collectionA.year;
  }

  // À année égale, H (hiver) est plus récent que E (été) : 2026H > 2026E
  if (collectionA.season !== collectionB.season) {
    if (collectionA.season === 'H' && collectionB.season === 'E') return -1;
    if (collectionA.season === 'E' && collectionB.season === 'H') return 1;
  }

  return 0;
}

/**
 * Trouve la valeur de "Première collection" la plus récente parmi un groupe de produits
 * 
 * @param products - Tableau de produits
 * @returns - La valeur de première collection la plus récente ou undefined
 */
export function getMostRecentCollectionTri(products: Product[]): string | undefined {
  const validCollections = products
    .map(p => p.collectionTri)
    .filter(pc => pc && pc.trim() !== '');
  
  if (validCollections.length === 0) return undefined;
  
  // Trier les collections et prendre la première (la plus récente)
  validCollections.sort(compareCollectionTri);
  return validCollections[0];
}

/**
 * Fonction de tri pour les produits groupés par modèle
 * Utilise la valeur de "Première collection" la plus récente du groupe
 * 
 * @param productsA - Premier groupe de produits
 * @param productsB - Deuxième groupe de produits
 * @returns - Nombre pour le tri
 */
export function sortProductGroupsByCollectionTri(
  productsA: Product[], 
  productsB: Product[]
): number {
  const collectionA = getMostRecentCollectionTri(productsA);
  const collectionB = getMostRecentCollectionTri(productsB);
  
  return compareCollectionTri(collectionA, collectionB);
}

/**
 * Fonction générique pour trier par ordre BO puis alphabétiquement
 * @param items - Items à trier (doivent avoir une propriété 'label')
 * @param getOrder - Fonction pour obtenir l'ordre d'un item
 * @returns Items triés
 */
export function sortByOrderThenAlphabetic<T>(
  items: T[],
  getOrder: (item: T) => number | null | undefined,
  getLabel: (item: T) => string
): T[] {
  return [...items].sort((a, b) => {
    const ordreA = getOrder(a);
    const ordreB = getOrder(b);
    
    // Si les deux ont un ordre défini
    if (ordreA != null && ordreB != null) {
      const orderComparison = ordreA - ordreB;
      if (orderComparison !== 0) return orderComparison;
    }
    // Si seul A a un ordre défini, A vient en premier
    else if (ordreA != null && ordreB == null) {
      return -1;
    }
    // Si seul B a un ordre défini, B vient en premier
    else if (ordreA == null && ordreB != null) {
      return 1;
    }
    
    // Si les ordres sont égaux ou les deux sont undefined/null, tri alphabétique
    const labelA = getLabel(a) || '';
    const labelB = getLabel(b) || '';
    return labelA.localeCompare(labelB);
  });
}